// composables/useDraggable.ts
import { ref, onMounted, onUnmounted, type Ref } from 'vue'

// Type definitions
interface Position {
  x: number
  y: number
}

export interface DragData {
  currentPosition: Position
  startPosition: Position
  deltaPosition: Position
  pointerId: number
  pointerType: string
}

interface DragOptions {
  onDragStart?: (event: PointerEvent, data: { startPosition: Position }) => void
  onDragMove?: (event: PointerEvent, data: DragData) => void
  onDragEnd?: (event: PointerEvent, data: DragData) => void
}

interface UseDraggableReturn {
  isDragging: Ref<boolean>
  pointerId: Ref<number | null>
  startPosition: Ref<Position>
  currentPosition: Ref<Position>
  deltaPosition: Ref<Position>
  handlePointerDown: (event: PointerEvent) => void
  handlePointerMove: (event: PointerEvent) => DragData | undefined
  handlePointerUp: (event: PointerEvent) => void
  handlePointerCancel: (event: PointerEvent) => void
}

export function useDraggable<T extends HTMLElement = HTMLElement>(elementRef: Ref<T | null>, options: DragOptions = {}): UseDraggableReturn {
  const { onDragStart = () => {}, onDragMove = () => {}, onDragEnd = () => {} } = options

  const isDragging = ref<boolean>(false)
  const pointerId = ref<number | null>(null)
  const startPosition = ref<Position>({ x: 0, y: 0 })
  const currentPosition = ref<Position>({ x: 0, y: 0 })
  const deltaPosition = ref<Position>({ x: 0, y: 0 })

  const handlePointerDown = (e: PointerEvent): void => {
    isDragging.value = true
    pointerId.value = e.pointerId

    startPosition.value = { x: e.clientX, y: e.clientY }
    currentPosition.value = { x: e.clientX, y: e.clientY }
    deltaPosition.value = { x: 0, y: 0 }

    if (elementRef.value) {
      elementRef.value.style.touchAction = 'none'
      elementRef.value.setPointerCapture(e.pointerId)
    }

    onDragStart(e, { startPosition: startPosition.value })
  }

  const handlePointerMove = (e: PointerEvent): DragData | undefined => {
    if (isDragging.value && e.pointerId === pointerId.value) {
      //e.preventDefault()
      //e.stopPropagation()
      //e.stopImmediatePropagation()

      currentPosition.value = { x: e.clientX, y: e.clientY }
      deltaPosition.value = {
        x: currentPosition.value.x - startPosition.value.x,
        y: currentPosition.value.y - startPosition.value.y,
      }

      const dragData: DragData = {
        currentPosition: currentPosition.value,
        startPosition: startPosition.value,
        deltaPosition: deltaPosition.value,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
      }

      onDragMove(e, dragData)

      return dragData
    }

    return undefined
  }

  const handlePointerUp = (e: PointerEvent): void => {
    if (e.pointerId === pointerId.value) {
      const dragData: DragData = {
        currentPosition: currentPosition.value,
        startPosition: startPosition.value,
        deltaPosition: deltaPosition.value,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
      }

      onDragEnd(e, dragData)

      isDragging.value = false
      pointerId.value = null

      if (elementRef.value) {
        elementRef.value.style.touchAction = 'auto'
        elementRef.value.releasePointerCapture(e.pointerId)
      }
    }
  }

  const handlePointerCancel = (e: PointerEvent): void => {
    if (e.pointerId === pointerId.value) {
      isDragging.value = false
      pointerId.value = null
    }
    if (elementRef.value) {
      elementRef.value.style.touchAction = 'auto'
    }
  }

  onMounted(() => {
    if (elementRef.value) {
      elementRef.value.addEventListener('pointermove', handlePointerMove as EventListener, { passive: false })
      elementRef.value.addEventListener('pointercancel', handlePointerCancel as EventListener)
    }
  })

  onUnmounted(() => {
    if (elementRef.value) {
      elementRef.value.removeEventListener('pointermove', handlePointerMove as EventListener)
      elementRef.value.removeEventListener('pointercancel', handlePointerCancel as EventListener)
    }
  })

  return {
    isDragging,
    pointerId,
    startPosition,
    currentPosition,
    deltaPosition,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  }
}
